import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="map"
const NO_DATA_COLOR = '#777777'
// Nothing is requested from Google until the user accepts the consent overlay.
// Acceptance is stored as a timestamp and expires after CONSENT_TTL_MS.
const CONSENT_STORAGE_KEY = 'ph24_google_maps_consent'
const CONSENT_TTL_MS = 7 * 24 * 60 * 60 * 1000
export default class extends Controller {
  static values = {
    providersUrl: String,
    initialProviders: Array,
    apiKey: { type: String, default: "" },
    mapId: { type: String, default: "" }, // Add map ID value
    scrollOnHover: { type: Boolean, default: true },
    scrollOnClick: { type: Boolean, default: false },
    navigateOnClick: { type: Boolean, default: true },
    providerType: { type: String, default: "" },
    fitBounds: { type: Boolean, default: false }
  }
  
  // Add these static targets at the top of the class
  static targets = ["ratingButton", "priceButton", "consent"]

  connect() {
    this.activeMarker = null;
    this.markers = [];
    this.currentProviderIds = [];
    this.isLoading = false;
    this.isInitialLoad = true; // Add flag to track initial map load
    this.initialMapFittingDone = false; // New flag to track if we've already fit markers to the map
    this.hoverTimer = null; // Add property to track hover timer
    this.hoverDelay = 330; // 330ms delay for hover actions
    this.displayMode = 'rating'; // Track current display mode: 'rating' or 'price'
    this.providersData = []; // Store provider data for mode switching

    // Store bound references so removeEventListener works correctly
    this.boundHandleResize = this.handleResize.bind(this);
    this.boundHandleProvidersLoaded = this.handleProvidersLoaded.bind(this);
    this.boundHandleGlobalMapEvent = this.handleGlobalMapEvent.bind(this);

    // Add event listeners for viewport resize
    window.addEventListener('resize', this.boundHandleResize);

    // Add BOTH event listeners for provider loading
    this.element.addEventListener('providers:loaded', this.boundHandleProvidersLoaded);

    // Add a global window event listener as a backup
    window.addEventListener('map:addMarkers', this.boundHandleGlobalMapEvent);
    this.pendingProviders = [];
    this.pendingReplacesInitial = false;

    // Only users who already accepted get the (lazy) auto-load; everyone else waits for the click.
    if (!this.hasStoredConsent()) return;
    this.hideConsent();
    this.setupLazyLoading();
  }

  hasStoredConsent() {
    try {
      const raw = localStorage.getItem(CONSENT_STORAGE_KEY);
      // Exact integer only: parseInt would accept "1234x", and a future value is not a past consent
      const acceptedAt = /^\d+$/.test(raw) ? Number(raw) : NaN;
      const now = Date.now();
      if (Number.isFinite(acceptedAt) && acceptedAt <= now && now - acceptedAt < CONSENT_TTL_MS) return true;
      if (raw !== null) localStorage.removeItem(CONSENT_STORAGE_KEY);
      return false;
    } catch (_) {
      return false;
    }
  }

  storeConsent() {
    try {
      localStorage.setItem(CONSENT_STORAGE_KEY, String(Date.now()));
    } catch (_) {}
  }

  hideConsent() {
    if (this.hasConsentTarget) this.consentTarget.classList.add('hidden');
  }

  acceptConsent() {
    this.storeConsent();
    this.hideConsent();
    this.loadGoogleMapsAPI();
  }
  
  disconnect() {
    // Clean up resources when controller disconnects
    this.clearMarkers();
    if (this.map) {
      // Remove event listeners 
      if (this.mapIdleListener) {
        google.maps.event.removeListener(this.mapIdleListener);
      }
      google.maps.event.clearInstanceListeners(this.map);
    }
    window.removeEventListener('resize', this.boundHandleResize);

    // Remove custom event listener
    this.element.removeEventListener('providers:loaded', this.boundHandleProvidersLoaded);

    // Remove the global event listener too
    window.removeEventListener('map:addMarkers', this.boundHandleGlobalMapEvent);

    if (this.windowLoadHandler) {
      window.removeEventListener('load', this.windowLoadHandler);
      this.windowLoadHandler = null;
    }

    if (this.lazyLoadTimeout) {
      clearTimeout(this.lazyLoadTimeout);
      this.lazyLoadTimeout = null;
    }

    if (this.idleCallbackId && 'cancelIdleCallback' in window) {
      cancelIdleCallback(this.idleCallbackId);
      this.idleCallbackId = null;
    }

    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
  }

  setupLazyLoading() {
    const initObserverAndLoad = () => {
      const loadMap = () => {
        // Use requestIdleCallback so we don't block the main thread during scrolling/rendering
        if ('requestIdleCallback' in window) {
          this.idleCallbackId = requestIdleCallback(() => {
            this.idleCallbackId = null;
            if (this.element.isConnected) this.loadGoogleMapsAPI();
         }, { timeout: 2000 });
        } else {
          // Fallback
          this.lazyLoadTimeout = setTimeout(() => {
           this.lazyLoadTimeout = null;
           if (this.element.isConnected) this.loadGoogleMapsAPI();
         }, 200);
        }
      };

      // Fallback if IntersectionObserver is somehow missing
      if (!('IntersectionObserver' in window)) {
        loadMap();
        return;
      }

      // Set up the observer
      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            loadMap();
            // Stop observing once the map starts loading
            if (this.observer) this.observer.disconnect(); 
          }
        });
      }, {
        // Start loading when the map is 300px away from scrolling into view
        rootMargin: '300px 0px', 
        threshold: 0.01
      });

      // Start observing the map container
      this.observer.observe(this.element);
    };

    // To maximally prioritize FCP/LCP, wait until the page has loaded before observing
    if (document.readyState === 'complete') {
      this.lazyLoadTimeout = setTimeout(initObserverAndLoad, 100);
    } else {
      this.windowLoadHandler = () => {
       this.lazyLoadTimeout = setTimeout(initObserverAndLoad, 100);
     };
     window.addEventListener('load', this.windowLoadHandler, { once: true });
    }
  }
  
  handleResize() {
    if (this.map) {
      // Trigger a resize event on the map to fix rendering issues
      google.maps.event.trigger(this.map, 'resize');
    }
  }
  
  async loadGoogleMapsAPI() {
    if (this._googleMapsLoadStarted) return;
    this._googleMapsLoadStarted = true;

    try {
      // Extract Map ID early to use in setOptions for prefetching config
      let mapId = this.mapIdValue;
      if (!mapId) mapId = this.element.getAttribute('data-map-id-value');
      if (!mapId) mapId = 'aae134ab06e315c0fb711c83';
      this._resolvedMapId = mapId;

      // Dynamically import the loader to keep initial main bundle small
      const { setOptions, importLibrary } = await import('@googlemaps/js-api-loader');

      // Add mapIds here to prevent the 500ms synchronous block for style config loading 
      // when `new google.maps.Map` is initialized.
      setOptions({
        key: this.apiKeyValue,
        v: "weekly",
        language: 'de',
        mapIds: [mapId]
      });

      await Promise.all([
        importLibrary('maps'),
        importLibrary('marker'),
        importLibrary('places'),
      ]);

      // Yield back to the browser's event loop to prevent blocking rendering
      await new Promise(resolve => setTimeout(resolve, 0));
      if (!this.element.isConnected) return;

      this.initMap();
    } catch (error) {
      console.error("Error loading Google Maps API:", error);
      this._googleMapsLoadStarted = false;
    }
  }
  
  initMap() {
    const mapElement = this.element.querySelector('#google-map');
    if (!mapElement) {
      console.error("Map element not found");
      return;
    }
    
    // Create loading indicator
    this.createLoadingIndicator(mapElement);
    
    // Extract Map ID directly from DOM if Stimulus value is undefined
    let mapId = this._resolvedMapId || this.mapIdValue || this.element.getAttribute('data-map-id-value') || 'aae134ab06e315c0fb711c83';
    
    // Create a map centered on Germany with improved controls
    const map = new google.maps.Map(mapElement, {
      center: { lat: 51.1657, lng: 10.4515 },
      zoom: 6,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false,
      zoomControl: true,
      zoomControlOptions: {
        position: google.maps.ControlPosition.RIGHT_TOP
      },
      gestureHandling: 'greedy', // Makes the map more mobile-friendly
      maxZoom: 18,
      minZoom: 5,
      mapId: mapId, // Use our reliable mapId variable
      language: 'de' // Set language to German
    });
    
    // Improved info window options for better styling
    this.infoWindow = new google.maps.InfoWindow({
      maxWidth: 300,
      pixelOffset: new google.maps.Size(0, -5),
      disableAutoPan: false
    });
    
    // Save map instance
    this.map = map;
    
    // Trigger resize to ensure map renders properly
    setTimeout(() => {
      google.maps.event.trigger(map, 'resize');
    }, 500);
    
    // Load initial providers - use preloaded data if available, otherwise fetch
    this.loadInitialProviders();
    
    // Set up the map idle event listener after a delay to avoid initial triggers
    setTimeout(() => {
      // Add listener for map bounds change (with debounce)
      let boundsChangeTimeout;
      this.mapIdleListener = map.addListener('idle', () => {
        clearTimeout(boundsChangeTimeout);
        boundsChangeTimeout = setTimeout(() => {
          // Skip reloading providers if this is the initial load or if we're currently loading
          if (this.isInitialLoad) {
            this.isInitialLoad = false; // Reset the flag after first idle event
            return;
          }
          
          // Only reload if not currently loading
          if (!this.isLoading) {
            this.loadProvidersInView();
          }
        }, 500);
      });
    }, 1000); // Wait a second before setting up the idle listener
    
    // Close info window when clicking on the map
    map.addListener('click', () => {
      this.infoWindow.close();
      if (this.activeMarker) {
        this.resetMarker(this.activeMarker);
        this.activeMarker = null;
      }
    });
  }


showRating() {
  if (this.displayMode === 'rating') return;
  
  this.displayMode = 'rating';
  this.updateButtonStates();
  this.refreshMarkers();
  
  // Dispatch event to update filter dropdown
  window.dispatchEvent(new CustomEvent('map:modeChanged', { 
    detail: { mode: 'rating' } 
  }));
}

showPrice() {
  if (this.displayMode === 'price') return;
  
  this.displayMode = 'price';
  this.updateButtonStates();
  this.refreshMarkers();
  
  // Dispatch event to update filter dropdown
  window.dispatchEvent(new CustomEvent('map:modeChanged', { 
    detail: { mode: 'price' } 
  }));
}

updateButtonStates() {
  if (!this.hasRatingButtonTarget || !this.hasPriceButtonTarget) return;
  
  const activeClasses = ['bg-ph24_new_blue', 'text-white'];
  const inactiveClasses = ['text-gray-700', 'hover:bg-gray-100'];
  
  if (this.displayMode === 'rating') {
    this.ratingButtonTarget.classList.add(...activeClasses);
    this.ratingButtonTarget.classList.remove(...inactiveClasses);
    
    this.priceButtonTarget.classList.remove(...activeClasses);
    this.priceButtonTarget.classList.add(...inactiveClasses);
  } else {
    this.priceButtonTarget.classList.add(...activeClasses);
    this.priceButtonTarget.classList.remove(...inactiveClasses);
    
    this.ratingButtonTarget.classList.remove(...activeClasses);
    this.ratingButtonTarget.classList.add(...inactiveClasses);
  }
}

refreshMarkers() {
  // Clear existing markers
  this.clearMarkers();
  
  // Re-add markers with the current display mode
  if (this.providersData && this.providersData.length > 0) {
    this.addMarkers(this.providersData, false, true);
  }
}

getPriceDisplay(priceCategory) {
  switch(priceCategory) {
    case 'below_average':
      return { symbol: '€', category: 'Preiswert' };
    case 'average':
      return { symbol: '€€', category: 'Durchschnitt' };
    case 'above_average':
      return { symbol: '€€€', category: 'Premium' };
    default:
      return { symbol: '?', category: 'Keine Daten' };
  }
}

  
  createLoadingIndicator(mapElement) {
    // Create a loading overlay for the map with styled design matching pins
    const loadingOverlay = document.createElement('div');
    loadingOverlay.className = 'absolute inset-0 bg-white bg-opacity-80 flex items-center justify-center z-10 hidden';
    loadingOverlay.innerHTML = `
      <div class="bg-white p-5 rounded-lg shadow-lg flex items-center flex-col">
        <div class="flex space-x-2 mb-3">
          <!-- Color dots matching pin rating colors -->
          <div class="w-3 h-3 rounded-full bg-[#06B579] animate-pulse"></div>
          <div class="w-3 h-3 rounded-full bg-[#1E88E5] animate-pulse" style="animation-delay: 0.2s"></div>
          <div class="w-3 h-3 rounded-full bg-[#F57C00] animate-pulse" style="animation-delay: 0.4s"></div>
          <div class="w-3 h-3 rounded-full bg-[#D32F2F] animate-pulse" style="animation-delay: 0.6s"></div>
        </div>
        <div class="marker-container flex items-center justify-center mb-3" style="background-color: #1E88E5; color: white; border-radius: 8px; padding: 6px 12px; font-weight: bold; font-size: 16px; box-shadow: 0 2px 4px rgba(0,0,0,0.5); min-width: 36px; white-space: nowrap; border: 1px solid #333;">
          <div class="animate-pulse">...</div>
        </div>
        <span class="text-ph24_blue font-medium text-sm">Lädt Pflegeanbieter...</span>
      </div>
    `;
    
    mapElement.parentNode.insertBefore(loadingOverlay, mapElement);
    this.loadingOverlay = loadingOverlay;
  }
  
  showLoading() {
    this.isLoading = true;
    if (this.loadingOverlay) {
      this.loadingOverlay.classList.remove('hidden');
    }
  }
  
  hideLoading() {
    this.isLoading = false;
    if (this.loadingOverlay) {
      this.loadingOverlay.classList.add('hidden');
    }
  }
  
  loadProvidersInView() {
    // Get current bounds
    const bounds = this.map.getBounds();
    if (!bounds) return;
    
    const ne = bounds.getNorthEast();
    const sw = bounds.getSouthWest();
    
    // Calculate center point
    const center = this.map.getCenter();
    
    // Build URL with bounds parameters
    let url = new URL(this.providersUrlValue, window.location.origin);
    let params = new URLSearchParams(url.search);
    
    // Add bounds parameters for reference
    params.set('ne_lat', ne.lat());
    params.set('ne_lng', ne.lng());
    params.set('sw_lat', sw.lat());
    params.set('sw_lng', sw.lng());
    
    // Add center coordinates for proximity search
    params.set('lat', center.lat());
    params.set('lng', center.lng());
    params.set('map_moved', 'true');

    // Add provider type if set
    if (this.providerTypeValue) {
      params.set('provider_type', this.providerTypeValue);
    }

    // Keep existing search parameters
    if (window.location.search) {
      const currentParams = new URLSearchParams(window.location.search);
      if (currentParams.has('query')) {
        params.set('query', currentParams.get('query'));
      }
      if (!this.providerTypeValue && currentParams.has('provider_type')) {
        params.set('provider_type', currentParams.get('provider_type'));
      }
      if (currentParams.has('min_rating')) {
        params.set('min_rating', currentParams.get('min_rating'));
      }
      if (currentParams.has('sort')) {
        params.set('sort', currentParams.get('sort'));
      }
      if (currentParams.has('max_price')) {
        params.set('max_price', currentParams.get('max_price'))
      }
      if (currentParams.has('verified_only')) {
        params.set('verified_only', currentParams.get('verified_only'));
      }
    }
    
    url.search = params.toString();
    
    // Show loading indicator
    this.showLoading();
    
    // Fetch providers
  fetch(url, {
      headers: {
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      }
    })
      .then(response => {
        if (!response.ok) {
          throw new Error(`Network response was not ok: ${response.status}`);
        }
        return response.json();
      })
      .then(data => {
        // Update URL without refreshing page
        const newUrl = new URL(window.location);
        newUrl.searchParams.set('ne_lat', ne.lat());
        newUrl.searchParams.set('ne_lng', ne.lng());
        newUrl.searchParams.set('sw_lat', sw.lat());
        newUrl.searchParams.set('sw_lng', sw.lng());
        window.history.replaceState({}, '', newUrl);
        
        // Clear existing markers
        this.clearMarkers();
        
        // Fix: Extract providers from the response data
        const providers = data.providers || data;
        
        // Make sure providers is actually an array before proceeding
        if (!Array.isArray(providers)) {
          this.hideLoading();
          return;
        }

        // Decide whether to update the list HTML
        const newProviderIds = providers.map(p => p.id);
        const sortParam = (new URL(window.location.href)).searchParams.get('sort');
        const sameSet = this.haveSameIds(this.currentProviderIds, newProviderIds);
        const shouldReplaceList = data.html && (!sortParam || !sameSet);

        if (shouldReplaceList) {
          const resultsContainer = document.querySelector('.provider-results-container');
          if (resultsContainer) {
            resultsContainer.innerHTML = data.html;
            // Update results count
            const countElement = document.querySelector('.results-count');
            if (countElement && data.total_count !== undefined) {
              countElement.textContent = data.total_count;
            }
            // Safely reconnect Stimulus controllers
            if (window.Stimulus && window.Stimulus.application) {
              const application = window.Stimulus.application;
              if (application.controllers) {
                application.controllers.forEach(controller => {
                  if (controller.element && document.body.contains(controller.element)) {
                    controller.disconnect();
                    controller.connect();
                  }
                });
              }
            }
          }
        }
        
        // Add new markers without recentering
        this.addMarkers(providers, false, true); // Pass false to never recenter
        
        // Only fit map for initial search with a query parameter and when we haven't done map fitting before
        if (params.has('query') && !this.initialMapFittingDone) {
          this.fitMapToSearchResults();
          this.initialMapFittingDone = true;
        }

  // Update current provider IDs to reflect the latest data powering markers
  this.currentProviderIds = newProviderIds;

  // Hide loading indicator
        this.hideLoading();
      })
      .catch(error => {
        this.hideLoading();
      });
  }
  
  loadInitialProviders() {
    // Check if we have specific coordinates in the URL
    const urlParams = new URLSearchParams(window.location.search);
    const hasCoordinates = urlParams.get('lat') && urlParams.get('lng');
    const shouldFitBounds = hasCoordinates || this.fitBoundsValue;
    const pending = this.pendingProviders;
    this.pendingProviders = [];

    // A search/filter happened before consent: the queued set supersedes the server payload
    if (this.pendingReplacesInitial) {
      this.pendingReplacesInitial = false;
      this.addMarkers(pending, shouldFitBounds, true);
      this.currentProviderIds = pending.map(p => p.id);
      this.initialMapFittingDone = true;
      return;
    }

    // Check if we have initial providers data from the server
    if (this.hasInitialProvidersValue && Array.isArray(this.initialProvidersValue) && this.initialProvidersValue.length > 0) {
      // Use the preloaded data - no loading indicator needed since it's instant
      this.addMarkers(this.initialProvidersValue, shouldFitBounds, true);
      this.initialMapFittingDone = true;
      if (pending.length > 0) this.addMarkers(pending, false, false);
      this.currentProviderIds = this.providersData.map(p => p.id);
      return;
    }
    
    // Fallback to AJAX if no initial data is available
    this.loadProviders();
  }
  
  loadProviders() {
    this.showLoading();
    
    // Check if we have a search query with coordinates in the URL
    const urlParams = new URLSearchParams(window.location.search);
    const hasSearchCoordinates = urlParams.get('lat') && urlParams.get('lng');
    const isDirectSearch = hasSearchCoordinates && urlParams.has('query');
    
    // Build providers URL by merging current page/sort-related params
    let initialUrl;
    try {
      initialUrl = new URL(this.providersUrlValue, window.location.origin);
    } catch (_e) {
      initialUrl = new URL(window.location.pathname + '.json', window.location.origin);
    }
    const merged = new URLSearchParams(initialUrl.search);
    const current = new URLSearchParams(window.location.search);
    if (this.providerTypeValue) {
      merged.set('provider_type', this.providerTypeValue);
    }
    ['page', 'sort', 'query', 'provider_type', 'min_rating', 'max_price', 'verified_only'].forEach((key) => {
      if (current.has(key)) merged.set(key, current.get(key));
    });
    initialUrl.search = merged.toString();

    fetch(initialUrl.toString(), {
      headers: {
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      }
    })
      .then(response => response.json())
      .then(data => {
        // Fix: Check for the correct data structure
        // Sometimes we get an object with a providers property, sometimes we get the array directly
        const providers = data.providers || data;
        
        // Make sure providers is actually an array before proceeding
        if (!Array.isArray(providers)) {
          this.hideLoading();
          return;
        }

        const shouldFitBounds = (hasSearchCoordinates || this.fitBoundsValue) && !this.initialMapFittingDone;
        
        // Add markers and specify whether to fit bounds (only on first load)
        this.addMarkers(providers, shouldFitBounds, true);

        // Track current provider IDs for later comparison on map moves
        if (Array.isArray(providers)) {
          this.currentProviderIds = providers.map(p => p.id);
        }
        
        // If this is a search result with coordinates, ensure proper zoom
        if ((isDirectSearch || this.fitBoundsValue) && !this.initialMapFittingDone) {
          this.fitMapToAllMarkers();
          this.initialMapFittingDone = true;
        }
        
        this.hideLoading();
      })
      .catch(error => {
        this.hideLoading();
      });
  }
  
  fitMapToSearchResults() {
    if (!this.markers || this.markers.length === 0) {
      return;
    }
    
    // For search results, ensure all markers are visible
    this.fitMapToAllMarkers();
  }
  
  fitMapToAllMarkers() {
    if (!this.markers || this.markers.length === 0) {
      return;
    }
    
    const bounds = new google.maps.LatLngBounds();
    let hasValidMarker = false;
    
    // Add all marker positions to bounds
    this.markers.forEach(marker => {
      if (marker.position) {
        bounds.extend(marker.position);
        hasValidMarker = true;
      }
    });
    
    if (hasValidMarker) {
      // Fit the map to the markers
      this.map.fitBounds(bounds);
      
      // Add some padding for better visibility
      const padding = { top: 50, right: 50, bottom: 50, left: 50 };
      this.map.fitBounds(bounds, padding);
      
      // Set a reasonable zoom level if there's only one result
      if (this.markers.length === 1) {
        this.map.setZoom(14); // Good zoom level for a single location
      } else if (this.markers.length <= 5) {
        // For a small number of results, don't zoom out too far
        const currentZoom = this.map.getZoom();
        if (currentZoom < 10) {
          this.map.setZoom(Math.min(12, currentZoom + 2));
        }
      } else {
        // For many results, limit how far we zoom out
        const currentZoom = this.map.getZoom();
        if (currentZoom < 8) {
          this.map.setZoom(9);
        }
      }
    }
  }
  
  // Providers pushed before consent are queued and drawn once the map is ready.
  queuePendingMarkers(providers, contextSwitch) {
    if (contextSwitch) {
      this.pendingProviders = [];
      this.pendingReplacesInitial = true;
    }
    this.pendingProviders = this.pendingProviders.concat(providers || []);
  }

  clearMarkers() {
    this.markers.forEach(marker => {
      marker.map = null;
    });
    this.markers = [];
    this.activeMarker = null;
  }

  haveSameIds(arr1, arr2) {
    if (!Array.isArray(arr1) || !Array.isArray(arr2)) return false;
    if (arr1.length !== arr2.length) return false;
    const a = [...arr1].sort((x, y) => x - y);
    const b = [...arr2].sort((x, y) => x - y);
    for (let i = 0; i < a.length; i++) {
      if (a[i] !== b[i]) return false;
    }
    return true;
  }
  
  /**
   * Add provider markers to the map.
   * @param {Array} providers - Array of provider objects.
   * @param {boolean} [shouldFitBounds=false] - If true, fit map to markers.
   * @param {boolean} [contextSwitch=true] - If true, clear existing markers.
   */
  addMarkers(providers, shouldFitBounds = false, contextSwitch = true) {
    if (!this.map) {
      this.queuePendingMarkers(providers, contextSwitch);
      return;
    }
    const bounds = new google.maps.LatLngBounds();
    let validMarkersCount = 0;
    
    // Keep providersData in sync with what is on the map: replace on context switch,
    // merge (deduped by id) on append so mode switches redraw the full set.
    if (contextSwitch) {
      this.clearMarkers();
      this.providersData = providers || [];
    } else {
      const known = new Set(this.providersData.map(p => p.id));
      this.providersData = this.providersData.concat((providers || []).filter(p => !known.has(p.id)));
    }
    
    if (!providers || providers.length === 0) {
      return;
    }
    
    providers.forEach((provider, index) => {
      // Ensure we have valid coordinates
      if (!provider.coordinates || 
          !Array.isArray(provider.coordinates) || 
          provider.coordinates.length < 2 ||
          !provider.coordinates[0] || 
          !provider.coordinates[1]) {
        return;
      }
      
      const lat = parseFloat(provider.coordinates[0]);
      const lng = parseFloat(provider.coordinates[1]);
      
      // Skip if coordinates are not valid numbers
      if (isNaN(lat) || isNaN(lng)) {
        return;
      }
      
      const position = { lat, lng };
      
      // Determine what to display based on mode
      let displayContent, displayColor, displayCategory;
      
      if (this.displayMode === 'price') {
        // Price display mode
        const priceInfo = this.getPriceDisplay(provider.price_category);
        displayContent = priceInfo.symbol;
        displayCategory = priceInfo.category;
        
        // Use consistent colors for price categories
        switch(provider.price_category) {
          case 'below_average':
            displayColor = '#E9EDF5'; // Green
            break;
          case 'average':
            displayColor = '#7A9CC6'; // Blue
            break;
          case 'above_average':
            displayColor = '#243A5E'; // Red
            break;
          default:
            displayColor = NO_DATA_COLOR; // Grey
        }
      } else {
        // Rating display mode (existing logic)
        let mdkRating = null;
        let originalGoogleRating = null;
        
        if (provider.google_rating !== undefined && provider.google_rating !== null) {
          originalGoogleRating = parseFloat(provider.google_rating);
          mdkRating = 6 - originalGoogleRating;
        }
        
        displayContent = (provider.google_rating !== null && provider.google_rating !== undefined && !isNaN(parseFloat(provider.google_rating))) 
          ? parseFloat(provider.google_rating).toFixed(1).replace('.', ',') 
          : "-";
        
        displayColor = NO_DATA_COLOR;
        displayCategory = 'Keine Bewertung';
        
        if (mdkRating !== null && !isNaN(parseFloat(provider.google_rating))) {
          if (mdkRating <= 1.5) {
            displayColor = '#06B579';
            displayCategory = 'Sehr gut';
          } else if (mdkRating <= 2.5) {
            displayColor = '#1E88E5';
            displayCategory = 'Gut';
          } else if (mdkRating <= 3.5) {
            displayColor = '#F57C00';
            displayCategory = 'Befriedigend';
          } else {
            displayColor = '#D32F2F';
            displayCategory = 'Mangelhaft';
          }
        }
      }

      if (displayColor == NO_DATA_COLOR) {
        // remove missing google or price data from map (still shows up in list)
        return;
      }
      
      const providerType = this.getProviderType(provider);
      const colorOptions = this.getDefaultMarkerOptions(providerType);
      
      // Create the custom marker content
      const markerContent = document.createElement("div");
      markerContent.className = "custom-marker";
      markerContent.innerHTML = `
        <div class="marker-container" style="background-color: ${displayColor}; color: white; border-radius: 8px; padding: 6px 8px; font-weight: bold; font-size: 16px; box-shadow: 0 2px 4px rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; min-width: 36px; white-space: nowrap; transition: all 0.2s ease-in-out; border: 1px solid #333; z-index: 1;">
          ${displayContent}
        </div>
        <div class="hover-card" style="display: none; position: absolute; background: white; border-radius: 8px; box-shadow: 0 4px 15px rgba(0,0,0,0.3); width: 220px; padding: 12px; z-index: 10000; pointer-events: none; border: 1px solid #ccc;">
          <div style="font-weight: bold; font-size: 14px; color: #003e7a; margin-bottom: 4px; white-space: normal;">${provider.display_name || provider.name}</div>
          <div style="font-size: 13px; color: #333; display: flex; align-items: center; margin-bottom: 6px;">
            <span style="background-color: ${displayColor}; color: white; padding: 2px 6px; border-radius: 4px; font-weight: bold; margin-right: 6px; border: 1px solid #333;">${displayContent}</span>
            <span class="font-medium">${displayCategory}</span>
          </div>
          <div style="font-size: 12px; color: #333; margin-top: 6px; font-weight: 500;">${provider.full_address || ''}</div>
          ${provider.distance_km ? `<div style="font-size: 12px; color: #666; margin-top: 4px; font-weight: 500;">Entfernung: ${provider.distance_km} km</div>` : ''}
        </div>
      `;
    
      // For advanced marker, load the required library
      const { AdvancedMarkerElement } = google.maps.marker;
    
      // Create the advanced marker
      const marker = new AdvancedMarkerElement({
        map: this.map,
        position: position,
        title: provider.name,
        content: markerContent
      });
      
      // Store additional data on marker
      marker.providerId = provider.id;
      marker.providerType = providerType;
      marker.displayColor = displayColor; // Store for hover effects
    
      // Get the correct URL based on provider type
      let detailUrl = this.getProviderUrl(provider);
      // Get type display for popup
      const typeDisplay = this.getProviderTypeDisplay(provider);
      // Get the display name from API response
      const displayName = provider.display_name || provider.name;
      
      // Define custom content for the info window
      const contentString = `
        <a href="${detailUrl}" class="block w-full">
          <div class="w-72 max-w-xs bg-white rounded-lg overflow-hidden shadow-lg border border-gray-200 hover:border-ph24_new_yellow transition-all duration-300">
            <div class="p-4 bg-gradient-to-r from-ph24_blue/10 to-ph24_blue/5">
              <div class="flex items-start">
                <!-- Logo area with dynamic background -->
                <div class="flex-shrink-0 mr-3">
                  <div class="w-14 h-14 rounded-lg ${provider.is_nursing_service ? 'bg-blue-50 border-blue-100' : provider.is_nursing_home ? 'bg-blue-50 border-blue-100' : provider.is_day_care ? 'bg-purple-50 border-purple-100' : 'bg-green-50 border-green-100'} flex items-center justify-center overflow-hidden shadow-sm border">
                    ${provider.logo_url 
                      ? `<img src="${provider.logo_url}" alt="${displayName} Logo" class="w-full h-full object-contain p-1.5">`
                      : colorOptions.svgIcon
                    }
                  </div>
                </div>
                
                <!-- Provider info -->
                <div>
                  <h3 class="text-sm font-semibold text-ph24_blue leading-tight">${displayName}</h3>
                  <div class="flex items-center mt-1.5">
                    <span class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-ph24_orange/90 text-white">
                      ${typeDisplay}
                    </span>
                    <span class="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium" 
                        style="background-color: ${displayColor}; color: white;">
                      ${displayContent}
                    </span>
                  </div>
                </div>
              </div>
            </div>
            
            <div class="p-3 border-t border-gray-100">
              <!-- Address -->
              <div class="flex items-start mb-2">
                <svg class="h-4 w-4 mt-0.5 mr-1.5 text-ph24_blue/60 flex-shrink-0" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <p class="text-xs text-gray-600 line-clamp-2">${provider.full_address || ''}</p>
              </div>
              
              <!-- Distance if available -->
              ${provider.distance_km 
                ? `<div class="flex items-start mb-2">
                    <svg class="h-4 w-4 mt-0.5 mr-1.5 text-ph24_blue/60 flex-shrink-0" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                    </svg>
                    <span class="text-xs text-gray-600">${provider.distance_km} km entfernt</span>
                  </div>`
                : ''
              }
              
              <!-- Phone if available -->
              ${provider.telephone 
                ? `<div class="flex items-start mb-2">
                    <svg class="h-4 w-4 mt-0.5 mr-1.5 text-ph24_blue/60 flex-shrink-0" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                    <span class="text-xs text-ph24_blue truncate">${provider.telephone}</span>
                  </div>`
                : ''
              }
              
              <!-- Services if available -->
              ${provider.service_specialties && provider.service_specialties.length > 0 
                ? `<div class="mt-2 border-t border-gray-100 pt-2">
                    <h4 class="text-xs font-medium text-ph24_blue_50 uppercase tracking-wide mb-1.5">Leistungen</h4>
                    <div class="flex flex-wrap gap-1.5">
                      ${provider.service_specialties.slice(0, 3).map(service => 
                        `<span class="px-2 py-0.5 bg-gray-100 text-ph24_blue text-xs rounded-md flex items-center">
                          <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3 mr-1 text-ph24_blue_50" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                          </svg>
                          ${service}
                        </span>`
                      ).join('')}
                    </div>
                  </div>`
                : ''
              }
            </div>
          </div>
        </a>
      `;
    
      // Add click event to marker
      marker.addEventListener("gmp-click", (e) => {
        // On mobile, show the hover card (not just info window)
        if (!this.isDesktop()) {
          // Hide all other hover-cards
          this.closeAllHoverCards();
          // Show this marker's hover-card
          const markerElement = marker.content;
          const hoverCard = markerElement.querySelector('.hover-card');
          if (hoverCard) {
            this.positionHoverCard(marker, hoverCard);
            hoverCard.style.display = 'block';
          }
          // Set this marker as active for styling
          this.setActiveMarker(marker);
          // Prevent navigation
          if (e && typeof e.preventDefault === "function") e.preventDefault();
          return;
        }

        // Desktop: configurable behavior
        if (this.navigateOnClickValue) {
          window.location.href = detailUrl;
        } else if (this.scrollOnClickValue) {
          this.setActiveMarker(marker);
          this.scrollToCard(provider.id);
          if (e && typeof e.preventDefault === 'function') e.preventDefault();
        } else {
          // Fallback: show info window
          this.infoWindow.setContent(contentString);
          this.infoWindow.open({
            anchor: marker,
            map: this.map,
            shouldFocus: false
          });
          this.infoWindow.setZIndex(2000);
        }
      });
      
      // Get marker DOM element for hover effects
      const markerElement = marker.content;
      const hoverCard = markerElement.querySelector('.hover-card');

      // --- MOBILE TAP HANDLING ---
      if (!this.isDesktop()) {
        // On mobile, tap marker to show hover card, tap hover card to navigate
        markerElement.addEventListener('click', (e) => {
          // Hide all other hover-cards
          this.closeAllHoverCards();
          // Show this marker's hover-card
          if (hoverCard) {
            this.positionHoverCard(marker, hoverCard);
            hoverCard.style.display = 'block';
          }
          // Set this marker as active for styling
          this.setActiveMarker(marker);
          // Prevent navigation
          if (e && typeof e.preventDefault === "function") e.preventDefault();
        });

        // On mobile, clicking the hover-card navigates to the provider show page
        if (hoverCard) {
          hoverCard.style.pointerEvents = 'auto'; // Make sure it's clickable
          hoverCard.addEventListener('click', (e) => {
            window.location.href = detailUrl;
          });
        }
      } else {
        // Desktop: configurable behavior
        markerElement.addEventListener('click', (e) => {
          if (this.navigateOnClickValue) {
            window.location.href = detailUrl;
          } else if (this.scrollOnClickValue) {
            this.setActiveMarker(marker);
            this.scrollToCard(provider.id);
            if (e && typeof e.preventDefault === 'function') e.preventDefault();
          }
        });
      }

      // Improved hover effects with dynamic positioning
      markerElement.addEventListener('mouseenter', () => {
        if (marker !== this.activeMarker) {
          // Visual effects happen immediately
          const darkerColor = this.darkenColor(displayColor, 0.2);
          markerElement.querySelector('.marker-container').style.backgroundColor = darkerColor;
          markerElement.querySelector('.marker-container').style.transform = 'scale(1.1)';
          
          // Position the hover card intelligently before showing it
          this.positionHoverCard(marker, hoverCard);
          hoverCard.style.display = 'block';
          
          // Bring this marker to front while hovering
          this.bringMarkerToFront(marker);
          
          // Highlight card immediately (visually) but delay scrolling
          this.highlightCardWithoutScroll(provider.id);
          
          // Only scroll to card on desktop if allowed
          if (this.isDesktop() && this.scrollOnHoverValue) {
            if (this.hoverTimer) clearTimeout(this.hoverTimer);
            this.hoverTimer = setTimeout(() => {
              this.scrollToCard(provider.id);
            }, this.hoverDelay);
          }
        }
      });
      
      markerElement.addEventListener('mouseleave', () => {
        if (marker !== this.activeMarker) {
          // Clear hover timer to prevent scrolling if user moved away quickly
          if (this.hoverTimer) {
            clearTimeout(this.hoverTimer);
            this.hoverTimer = null;
          }
          
          markerElement.querySelector('.marker-container').style.backgroundColor = displayColor;
          markerElement.querySelector('.marker-container').style.transform = 'scale(1)';
          hoverCard.style.display = 'none';
          
          // Restore normal z-index
          marker.zIndex = 1;
          
          // Un-highlight provider card
          this.unhighlightCard(provider.id);
        }
      });
      
      bounds.extend(position);
      this.markers.push(marker);
      validMarkersCount++;
    });
    
    // SIMPLIFIED: Only fit bounds if explicitly told to do so and we have markers
    if (shouldFitBounds && validMarkersCount > 0) {
      this.map.fitBounds(bounds);
      // If there's only one marker, set a fixed zoom level
      if (validMarkersCount === 1) {
        const listener = google.maps.event.addListener(this.map, "idle", () => {
          this.map.setZoom(14);
          google.maps.event.removeListener(listener);
        });
      }
      
      // Mark that we've done the initial map fitting
      this.initialMapFittingDone = true;
    }
  }
  
  // Helper function to darken a color
  darkenColor(color, amount) {
    // Convert hex to RGB
    let r, g, b;
    
    if (color.startsWith('#')) {
      r = parseInt(color.substring(1, 3), 16);
      g = parseInt(color.substring(3, 5), 16);
      b = parseInt(color.substring(5, 7), 16);
    } else if (color.startsWith('rgb')) {
      const rgbMatch = color.match(/^rgb\s*\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*\)$/i);
      if (rgbMatch) {
        r = parseInt(rgbMatch[1]);
        g = parseInt(rgbMatch[2]);
        b = parseInt(rgbMatch[3]);
      } else {
        return color; // Return original if can't parse
      }
    } else {
      return color; // Return original if unknown format
    }
    
    // Darken the RGB values
    r = Math.max(0, Math.floor(r * (1 - amount)));
    g = Math.max(0, Math.floor(g * (1 - amount)));
    b = Math.max(0, Math.floor(b * (1 - amount)));
    
    // Convert back to hex
    return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
  }
  
  // Helper function to reset marker to default state
  resetMarker(marker) {
    if (marker.content) {
      // Get the original color from the marker container
      const container = marker.content.querySelector('.marker-container');
      const hoverCard = marker.content.querySelector('.hover-card');
      
      // Get the computed style to find the original color
      if (container) {
        // Get the background color from the inline style
        const bgColorStyle = container.style.backgroundColor;
        container.style.backgroundColor = bgColorStyle;
        container.style.transform = 'scale(1)';
        container.style.zIndex = '1';
      }
      if (hoverCard) {
        hoverCard.style.display = 'none';
      }
    }
  }
  
  // Helper function to set active marker
  setActiveMarker(marker) {
    if (marker.content) {
      const container = marker.content.querySelector('.marker-container');
      const hoverCard = marker.content.querySelector('.hover-card');
      if (container) {
        container.style.backgroundColor = '#06B579'; // Green active color
        container.style.transform = 'scale(1.1)';
        container.style.zIndex = '1000';
      }
      if (hoverCard) {
        // Position hover card intelligently before showing
        this.positionHoverCard(marker, hoverCard);
        hoverCard.style.display = 'block';
      }
    }
    this.activeMarker = marker;
    
    // Set higher zIndex to bring marker to front
    this.bringMarkerToFront(marker);
    
    // Also highlight the card in the list view
    this.highlightCardWithoutScroll(marker.providerId);

    // Only scroll to card on desktop and when configured
    if (this.isDesktop() && this.scrollOnClickValue) {
      this.scrollToCard(marker.providerId);
    }
  }

  // Add this method to ensure marker is at the front when active
  bringMarkerToFront(marker) {
    // Set a higher zIndex to bring marker to front
    marker.zIndex = 1000;
  }
  
  // Add method to unhighlight card without removing all highlights
  unhighlightCard(providerId) {
    // Only unhighlight if this is not the active marker
    if (this.activeMarker && this.activeMarker.providerId === providerId) {
      return;
    }
    
    const card = document.querySelector(`.provider-card[data-provider-id="${providerId}"]`);
    if (card) {
      card.classList.remove('ring-2', 'ring-ph24_green', 'shadow-lg');
      card.classList.add('shadow');
    }
  }
  
  // Add helper method to position hover card dynamically
  positionHoverCard(marker, hoverCard) {
    if (!marker || !hoverCard || !this.map) return;
    
    const markerPosition = marker.position;
    const mapBounds = this.map.getBounds();
    
    if (!markerPosition || !mapBounds) return;
    
    const mapDiv = this.map.getDiv();
    const mapHeight = mapDiv.offsetHeight;
    const mapWidth = mapDiv.offsetWidth;
    
    // Get marker's pixel coordinates
    const markerPoint = this.map.getProjection()?.fromLatLngToPoint(markerPosition);
    const topLeftPoint = this.map.getProjection()?.fromLatLngToPoint(
      new google.maps.LatLng(mapBounds.getNorthEast().lat(), mapBounds.getSouthWest().lng())
    );
    
    if (!markerPoint || !topLeftPoint) return;
    
    // Calculate position relative to the map container
    const scale = Math.pow(2, this.map.getZoom());
    const pixelX = (markerPoint.x - topLeftPoint.x) * scale;
    const pixelY = (markerPoint.y - topLeftPoint.y) * scale;
    
    // Determine whether marker is closer to top/bottom and left/right edges
    const isNearTop = pixelY < mapHeight * 0.3;
    const isNearBottom = pixelY > mapHeight * 0.7;
    const isNearLeft = pixelX < mapWidth * 0.3;
    const isNearRight = pixelX > mapWidth * 0.7;
    
    // Reset all positioning styles
    hoverCard.style.bottom = '';
    hoverCard.style.top = '';
    hoverCard.style.left = '';
    hoverCard.style.right = '';
    hoverCard.style.transform = '';
    
    // Position based on marker's location
    if (isNearTop) {
      // Position below marker if it's near the top
      hoverCard.style.top = '100%';
      hoverCard.style.transform = 'translateX(-50%) translateY(8px)';
    } else {
      // Default position above marker
      hoverCard.style.bottom = '100%';
      hoverCard.style.transform = 'translateX(-50%) translateY(-8px)';
    }
    
    // Adjust horizontal position if near edges
    if (isNearLeft) {
      hoverCard.style.left = '0';
      hoverCard.style.transform = isNearTop 
        ? 'translateY(8px)' 
        : 'translateY(-8px)';
    } else if (isNearRight) {
      hoverCard.style.right = '0';
      hoverCard.style.left = 'auto';
      hoverCard.style.transform = isNearTop 
        ? 'translateY(8px)' 
        : 'translateY(-8px)';
    }
  }
  
  // Helper function to determine marker options by provider type
  getMarkerOptionsByType(provider) {
    const type = this.getProviderType(provider);
    const options = this.getDefaultMarkerOptions(type);
    
    return {
      icon: {
        path: 'M 0,0 C -2,-20 -10,-22 -10,-30 A 10,10 0 1,1 10,-30 C 10,-22 2,-20 0,0 z M -2,-30 a 2,2 0 1,0 4,0 a 2,2 0 1,0 -4,0',
        fillColor: options.color,
        fillOpacity: 1,
        strokeColor: '#fff',
        strokeWeight: 2,
        scale: 1,
        anchor: new google.maps.Point(0, 0),
      },
      hoverColor: options.hoverColor,
      svgIcon: options.svgIcon
    };
  }
  
  // Get provider type
  getProviderType(provider) {
    if (provider.is_nursing_home) return 'nursing_home';
    if (provider.is_nursing_service) return 'nursing_service';
    if (provider.is_day_care) return 'day_care';
    if (provider.is_home_assistance) return 'home_assistance';
    return 'default';
  }
  
  // Get display name for provider type
  getProviderTypeDisplay(provider) {
    if (provider.is_nursing_home) return 'Pflegeheim';
    if (provider.is_nursing_service) return 'Pflegedienst';
    if (provider.is_day_care) return 'Tagespflege';
    if (provider.is_home_assistance) return 'Haushaltshilfe';
    return 'Anbieter';
  }
  
  // Get default marker options
  getDefaultMarkerOptions(type) {
    const options = {
      default: {
        color: '#EF5844',
        hoverColor: '#f06a5e',
        svgIcon: `<svg class="h-6 w-6 text-ph24_blue" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
        </svg>`
      },
      nursing_home: {
        color: '#3182CE', // Blue
        hoverColor: '#4299E1',
        svgIcon: `<svg class="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
        </svg>`
      },
      nursing_service: {
        color: '#E53E3E', // Red
        hoverColor: '#F56565',
        svgIcon: `<svg class="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>`
      },
      day_care: {
        color: '#805AD5', // Purple
        hoverColor: '#9F7AEA',
        svgIcon: `<svg class="h-6 w-6 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>`
      },
      home_assistance: {
        color: '#38A169', // Green
        hoverColor: '#48BB78',
        svgIcon: `<svg class="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
        </svg>`
      }
    };
    
    return options[type] || options.default;
  }
  
  // Method to handle when a card is clicked
  selectCard(event) {
    const providerId = parseInt(event.currentTarget.dataset.providerId, 10);
    const marker = this.findMarkerById(providerId);
    
    if (marker) {
      google.maps.event.trigger(marker, 'click');
      this.map.panTo(marker.getPosition());
    }
  }
  
  // Method to handle hovering over a list item
  highlightMarker(event) {
    const providerId = parseInt(event.currentTarget.dataset.providerId, 10);
    const marker = this.findMarkerById(providerId);
    
    if (marker && marker !== this.activeMarker) {
      const container = marker.content.querySelector('.marker-container');
      const hoverCard = marker.content.querySelector('.hover-card');
      if (container) {
        container.style.backgroundColor = '#0052a3'; // Lighter blue on hover
        container.style.transform = 'scale(1.1)';
      }
      if (hoverCard) {
        hoverCard.style.display = 'block';
      }
      
      // Bring marker to front while hovering
      this.bringMarkerToFront(marker);
      
      // Center on the marker to ensure it's visible
      this.map.panTo(marker.getPosition());
    }
  }
  
  // Method to handle mouse leaving a list item
  unhighlightMarker(event) {
    const providerId = parseInt(event.currentTarget.dataset.providerId, 10);
    const marker = this.findMarkerById(providerId);
    
    if (marker && marker !== this.activeMarker) {
      const container = marker.content.querySelector('.marker-container');
      const hoverCard = marker.content.querySelector('.hover-card');
      if (container) {
        // Get the original background color from the element's style
        const originalColor = container.getAttribute('data-original-color') || '#777777';
        container.style.backgroundColor = originalColor;
        container.style.transform = 'scale(1)';
      }
      if (hoverCard) {
        hoverCard.style.display = 'none';
      }
      // Restore normal z-index
      marker.zIndex = 1;
    }
  }
  
  findMarkerById(providerId) {
    return this.markers.find(m => m.providerId === providerId);
  }
  
  highlightCard(providerId) {
    this.highlightCardWithoutScroll(providerId);
    this.scrollToCard(providerId);
  }
  
  // New method to highlight card without scrolling
  highlightCardWithoutScroll(providerId) {
    // Remove highlight from all cards
    document.querySelectorAll('.provider-card').forEach(card => {
      card.classList.remove('ring-2', 'ring-ph24_green', 'shadow-lg');
      card.classList.add('shadow');
    });
    
    // Add highlight to the matching card
    const card = document.querySelector(`.provider-card[data-provider-id="${providerId}"]`);
    if (card) {
      card.classList.add('ring-2', 'ring-ph24_green', 'shadow-lg');
      card.classList.remove('shadow');
    }
  }
  
  // New method to handle scrolling with a specific card
  scrollToCard(providerId) {
    const card = document.querySelector(`.provider-card[data-provider-id="${providerId}"]`);
    if (card) {
      card.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'center'
      });
    }
  }

  // Add helper method to check if we're on desktop
  isDesktop() {
    return window.innerWidth >= 1024; // 1024px is typically the lg breakpoint in Tailwind
  }

  // Add helper method to get provider URL based on its type
  getProviderUrl(provider) {
    // Get the correct URL based on provider type
    let detailUrl = `/pflegeplatz/providers/${provider.id}`;
    if (this.providerTypeValue) {
      switch (this.providerTypeValue) {
      case "nursing_service":
        detailUrl = `/pflegedienste/${provider.slug}`;
        break;

      case "nursing_home":
        detailUrl = `/pflegeheime/${provider.slug}`;
        break;

      case "day_care":
        detailUrl = `/tagespflegen/${provider.slug}`;
        break;

      case "home_assistance":
        detailUrl = `/haushaltshilfen/${provider.slug}`;
        break;

      case "supply_store":
        detailUrl = `/sanitaetshaus/${provider.slug}`;
        break;
      }
   }
    return detailUrl;
  }

  // Add a new method to handle the global window event
  handleGlobalMapEvent(event) {
    const newProviders = event.detail?.providers;
    if (newProviders && Array.isArray(newProviders) && newProviders.length > 0) {
      this.addMarkers(newProviders, false, false);
    }
  }

  // Enhance the existing handler
  handleProvidersLoaded(event) {
    const newProviders = event.detail?.providers;

    if (newProviders && Array.isArray(newProviders) && newProviders.length > 0) {
      // Log the first provider for debugging
      if (newProviders.length > 0) {
      }
      
      // Add the new providers to the map
      this.addMarkers(newProviders, false, false);
    } else {
    }
  }

  // Add this helper to close all hover-cards
  closeAllHoverCards() {
    this.markers.forEach(marker => {
      if (marker.content) {
        const hoverCard = marker.content.querySelector('.hover-card');
        if (hoverCard) hoverCard.style.display = 'none';
      }
    });
  }
}